import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import LandingChrome from '../components/LandingChrome';
import { FAQ_DATA, FALLBACK_PLANS, landingFeaturesFromPlan } from '../data/marketingData';
import { useMarketingPlans } from '../hooks/useMarketingPlans';
import { formatMoney2, parseStrikeAmount } from '../utils/planPrice';
import './Landing.css';

const DOC_TITLE_EN = 'Pricing - Simple, transparent plans | ReplyTek';
const DOC_TITLE_ES = 'Precios - Planes simples y transparentes | ReplyTek';

export default function Pricing() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const apiPlans = useMarketingPlans();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = lang === 'en' ? DOC_TITLE_EN : DOC_TITLE_ES;
  }, [lang]);

  return (
    <LandingChrome>
      <main>
        <section className="section pricing" style={{ paddingTop: '6rem' }} id="pricing">
          <h1 className="section__title">
            {lang === 'en' ? 'Pricing - Simple, transparent plans' : 'Precios - Planes simples y transparentes'}
          </h1>
          <p className="section__subtitle">
            {lang === 'en' ? 'No credit card required · Cancel anytime' : 'No requiere tarjeta de crédito · Cancela cuando quieras'}
          </p>
          <div className="cards-3">
            {apiPlans && apiPlans.length > 0
              ? apiPlans.map((plan, i) => {
                  const featured = apiPlans.length >= 2 ? i === 1 : i === 0;
                  const feats = landingFeaturesFromPlan(plan, lang);
                  return (
                    <div className={`price-card ${featured ? 'price-card--featured' : ''}`} key={plan.id ?? i}>
                      {featured && (
                        <div className="price-card__badge">
                          {lang === 'en' ? 'Most Popular' : 'Más Popular'}
                        </div>
                      )}
                      <h3 className="price-card__name">{plan.title || '—'}</h3>
                      <div className="price-card__price">
                        {parseStrikeAmount(plan.price_strike) != null && (
                          <div className="plan-price-strike">
                            ${formatMoney2(parseStrikeAmount(plan.price_strike))}
                          </div>
                        )}
                        <div className="price-card__price-row">
                          <span className="price-card__currency">$</span>
                          <span className="price-card__amount">{formatMoney2(plan.price ?? 0)}</span>
                          <span className="price-card__period">/{lang === 'en' ? 'mo' : 'mes'}</span>
                        </div>
                      </div>
                      <ul className="price-card__features">
                        {feats.map((f, j) => (
                          <li key={j}>
                            <span className="check">✓</span> {f}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className={`btn-${featured ? 'primary' : 'ghost'} btn-full`}
                        onClick={() => navigate('/signup')}
                      >
                        {t('get_started')}
                      </button>
                    </div>
                  );
                })
              : FALLBACK_PLANS.map((plan, i) => (
                  <div className={`price-card ${plan.highlight ? 'price-card--featured' : ''}`} key={i}>
                    {plan.highlight && (
                      <div className="price-card__badge">{lang === 'en' ? 'Most Popular' : 'Más Popular'}</div>
                    )}
                    <h3 className="price-card__name">{plan.name[lang]}</h3>
                    <div className="price-card__price">
                      <div className="price-card__price-row">
                        <span className="price-card__currency">$</span>
                        <span className="price-card__amount">{formatMoney2(plan.price)}</span>
                        <span className="price-card__period">/{lang === 'en' ? 'mo' : 'mes'}</span>
                      </div>
                    </div>
                    <ul className="price-card__features">
                      {plan.features[lang].map((f, j) => (
                        <li key={j}>
                          <span className="check">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`btn-${plan.highlight ? 'primary' : 'ghost'} btn-full`}
                      onClick={() => navigate('/signup')}
                    >
                      {t('get_started')}
                    </button>
                  </div>
                ))}
          </div>
        </section>

        <section className="section faq" id="faq">
          <h2 className="section__title">
            {lang === 'en' ? 'Frequently asked questions' : 'Preguntas frecuentes'}
          </h2>
          <div className="faq__list">
            {FAQ_DATA.map((item, i) => (
              <div
                className={`faq__item ${openFaq === i ? 'faq__item--open' : ''}`}
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                role="presentation"
              >
                <div className="faq__question">
                  <span>{item.q[lang]}</span>
                  <span className="faq__chevron">{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && <div className="faq__answer">{item.a[lang]}</div>}
              </div>
            ))}
          </div>
        </section>
      </main>
    </LandingChrome>
  );
}
